// The board's word when timed effects run out (the 2026-10-15 session pass): the End of Round takes a round off every
// timed effect, and one that reaches zero is gone — in a fight "Stunned" vanished from the goblin with nothing said,
// and the DM had to remember it had lapsed. Read from the encounter this person holds (the DM's whole one, a player's
// projection): only what their screen already showed can be named.
import { useEffect, useRef } from 'react';
import { emptyEncounter } from '@dragons-table/vtt-shared';
import { useTableState } from './TableState';

interface Reading {
  running: boolean;
  round: number;
  combatants: ReadonlyArray<{ id: string; name?: string; effects?: ReadonlyArray<{ id: string; label: string; duration: unknown }> }>;
}

/** The timed effects that ran out as the round moved on: "Stunned on Goblin B". Nothing when no round was crossed forward. */
export function effectsEnded(before: Reading | null, after: Reading): string[] {
  if (!before || !before.running || !after.running || after.round <= before.round) return [];
  const now = new Map(after.combatants.map((c) => [c.id, new Set((c.effects ?? []).map((e) => e.id))]));
  const out: string[] = [];
  for (const c of before.combatants) {
    const still = now.get(c.id);
    if (!still) continue; // the combatant left the fight: its effects went with it
    for (const e of c.effects ?? []) if (e.duration && !still.has(e.id)) out.push(`${e.label} on ${c.name ?? 'an unnamed creature'}`);
  }
  return out;
}

/** The sentence: "Stunned on Goblin B ended with round 1." */
export function effectsEndedNotice(ended: string[], round: number): string | null {
  if (!ended.length) return null;
  const list = ended.length === 1 ? ended[0]! : `${ended.slice(0, -1).join(', ')} and ${ended[ended.length - 1]}`;
  return `${list} ended with round ${round}.`;
}

/** Says it on the board, once, when the round moves on. */
export function useEffectsEndedNotice(): void {
  const { store, setNotice } = useTableState();
  const reading = (store.state.encounter ?? emptyEncounter()) as Reading;
  const rewinds = store.rewinds ?? 0;
  const last = useRef<Reading | null>(null);
  const lastRewinds = useRef(rewinds);
  useEffect(() => {
    const before = last.current;
    last.current = reading;
    // A rewind (or its undo) can land on a later round than the one shown: that is the past restored, not a round ended.
    const rewound = rewinds !== lastRewinds.current;
    lastRewinds.current = rewinds;
    const said = before && !rewound ? effectsEndedNotice(effectsEnded(before, reading), before.round) : null;
    if (said) setNotice(said);
  }, [reading]); // eslint-disable-line react-hooks/exhaustive-deps
}
